import {
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit"
import { PreferenceId, Subject, SubjectId } from "./types"
import { createRestoreSnapshot, snapshotSlice } from "./snapshot"
import { dataLoaded, removePreference } from "./actions"
import { GetColours } from "@/constants/priorityColours"

const subjectsAdapter = createEntityAdapter<Subject>()
const selectors = subjectsAdapter.getSelectors()
const restoreSnapshot = createRestoreSnapshot(subjectsAdapter)

export const subjectsSlice = createSlice({
  name: "subjects",
  initialState: subjectsAdapter.getInitialState({
    snapshot: subjectsAdapter.getInitialState(),
  }),
  reducers: {},
  selectors: {
    selectSubjects: selectors.selectAll,
    selectSubjectColours: createSelector(
      [
        (state, { id }: { id: SubjectId }) => selectors.selectById(state, id),
        (_, { getColours }: { getColours: GetColours }) => getColours,
        (_, { rank }: { rank: number }) => rank,
      ],
      (subject, getColours, rank) =>
        getColours(`${subject.id}`, rank, subject.preferenceIds.length),
    ),
  },
  extraReducers(builder) {
    builder.addCase(dataLoaded, (state, action) => {
      subjectsAdapter.removeAll(state)

      subjectsAdapter.addMany(
        state,
        action.payload.map(({ preferences, id, ...subject }) => ({
          ...subject,
          id: SubjectId({ id }),
          preferenceIds: preferences.map((p) => PreferenceId({ id: p.id })),
        })),
      )
    })

    builder.addAsyncThunk(removePreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { preference } = action.meta.arg
        const { id, subjectId } = preference
        const subject = selectors.selectById(state, subjectId)

        if (!subject) {
          return state
        }

        subjectsAdapter.updateOne(state, {
          id: subjectId,
          changes: {
            preferenceIds: subject.preferenceIds.filter((p) => p !== id),
          },
        })
      },
      fulfilled: (state, action) => {
        const {
          preference: { subjectId },
        } = action.meta.arg
        const subject = selectors.selectById(state, subjectId)

        if (!subject) {
          return
        }

        if (subject.preferenceIds.length > 0) {
          return
        }

        subjectsAdapter.removeOne(state, subjectId)
      },
    })
  },
})

const { selectSubjects, selectSubjectColours } = subjectsSlice.selectors

const { selectById: selectSubject } = subjectsAdapter.getSelectors(
  subjectsSlice.selectSlice,
)

export const subjectSelectors = {
  selectSubjects,
  selectSubjectColours,
  selectSubject,
}
